import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Rocket, Building2, X, ChevronRight, ChevronLeft } from "lucide-react";

// Widget flotante de modelos de negocio (SaaS / Franquicia).
//  variant="public" (landing/paginas publicas): izquierda.
//    - Movil: boton redondo arriba del boton de TIENDA (z-40, bajo sidebar z-60).
//    - Escritorio: tab vertical en el borde izquierdo a media altura; revela al
//      hacer scroll (>300px) o al acercar el mouse al borde izquierdo.
//  variant="platform" (dentro de la plataforma logueada): derecha.
//    - Movil: icono flotante compacto a la derecha, encima de WhatsApp (z-40,
//      bajo el slide menu z-50 y el user menu z-60). No obstruye el menu inferior.
//    - Escritorio: tab vertical en el borde derecho a media altura; revela al
//      hacer scroll o al acercar el mouse al borde derecho.
export default function ModelosFloatWidget({ variant = "public" }) {
  const platform = variant === "platform";
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hoverEdge, setHoverEdge] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // En la plataforma el tab queda estático flotante (siempre visible); en el
  // landing público se revela al hacer scroll o al acercar el mouse al borde.
  const showTab = platform ? true : (visible || hoverEdge || open);
  const Arrow = platform ? ChevronLeft : ChevronRight;

  return (
    <>
      {/* Zona hover del borde (escritorio) */}
      <div
        className={`hidden md:block fixed top-0 h-full w-5 z-40 ${platform ? "right-0" : "left-0"}`}
        onMouseEnter={() => setHoverEdge(true)}
        onMouseLeave={() => setHoverEdge(false)}
        aria-hidden
      />

      {/* === Movil: boton flotante === */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Cerrar modelos de negocio" : "Ver modelos de negocio"}
        className={`md:hidden fixed z-40 transition-all duration-300 ${showTab ? "opacity-100" : "opacity-0 pointer-events-none"} ${
          platform
            ? `right-4 bottom-[calc(13rem+env(safe-area-inset-bottom))] ${showTab ? "translate-y-0" : "translate-y-4"}`
            : `left-4 bottom-[calc(11rem+env(safe-area-inset-bottom))] ${showTab ? "translate-y-0" : "translate-y-4"}`
        }`}
      >
        <span className={`absolute inset-0 bg-cyan animate-ping opacity-20 rounded-full ${platform ? "" : ""}`} style={{ animationDuration: "2.5s" }} />
        <span className={`relative ${platform ? "w-10 h-10" : "w-12 h-12"} rounded-full bg-cyan hover:bg-cyan-hover flex items-center justify-center shadow-lg shadow-cyan/40 glow-cyan transition-colors`}>
          {open ? <X className={`${platform ? "w-5 h-5" : "w-6 h-6"} text-ink`} /> : <Rocket className={`${platform ? "w-5 h-5" : "w-6 h-6"} text-ink`} />}
        </span>
      </button>

      {/* === Escritorio: tab vertical en el borde a media altura === */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Cerrar modelos de negocio" : "Ver modelos de negocio"}
        className={`hidden md:flex fixed top-1/2 -translate-y-1/2 z-50 transition-transform duration-300 ease-out ${
          platform
            ? `right-0 ${showTab ? "translate-x-0" : "translate-x-full"}`
            : `left-0 ${showTab ? "translate-x-0" : "-translate-x-full"}`
        }`}
      >
        <span className="absolute inset-0 bg-cyan animate-ping opacity-20" style={{ animationDuration: "2.5s" }} />
        <span className={`relative flex flex-col items-center gap-1.5 bg-cyan hover:bg-cyan-hover text-ink py-3 px-1.5 shadow-lg shadow-cyan/40 transition-colors ${platform ? "rounded-l-none" : ""}`}>
          {open ? <X className="w-5 h-5" /> : <Rocket className="w-5 h-5" />}
          <span className="font-display font-bold uppercase text-[0.62rem] tracking-[0.2em] [writing-mode:vertical-rl] rotate-180">Modelos</span>
        </span>
      </button>

      {/* Panel — posicion responsiva + lado segun variante */}
      {open && (
        <div className={`fixed z-40 md:z-50 w-[min(88vw,290px)] bg-ink-surface border border-ink-border shadow-2xl animate-in fade-in duration-200 ${
          platform
            ? "right-4 bottom-[calc(15rem+env(safe-area-inset-bottom))] md:right-14 md:bottom-auto md:top-1/2 md:-translate-y-1/2 slide-in-from-right-2 md:slide-in-from-right-4"
            : "left-4 bottom-[calc(13rem+env(safe-area-inset-bottom))] md:left-14 md:bottom-auto md:top-1/2 md:-translate-y-1/2 slide-in-from-left-2 md:slide-in-from-left-4"
        }`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-ink-border">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-cyan/15 flex items-center justify-center">
                <Rocket className="w-4 h-4 text-cyan" />
              </div>
              <Link to="/auditoria-valor" onClick={() => setOpen(false)} className="font-display font-bold text-sm hover:text-cyan transition-colors">Modelos de negocio</Link>
            </div>
            <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink-text p-1"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-3 space-y-2">
            <Link to="/planes-saas" onClick={() => setOpen(false)}
              className="group flex items-center gap-3 p-3 border border-ink-border hover:border-cyan hover:bg-cyan/5 transition-colors">
              <div className="w-10 h-10 bg-cyan/15 group-hover:bg-cyan/25 flex items-center justify-center shrink-0">
                <Rocket className="w-5 h-5 text-cyan" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm">SaaS / Suscripción</div>
                <div className="text-[0.7rem] text-ink-muted">Renta mensual · desde $899 MXN</div>
              </div>
              <Arrow className={`w-4 h-4 text-ink-muted group-hover:text-cyan ${platform ? "" : ""}`} />
            </Link>
            <Link to="/franquicia" onClick={() => setOpen(false)}
              className="group flex items-center gap-3 p-3 border border-ink-border hover:border-cyan hover:bg-cyan/5 transition-colors">
              <div className="w-10 h-10 bg-cyan/15 group-hover:bg-cyan/25 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-cyan" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm">Franquicia / White-Label</div>
                <div className="text-[0.7rem] text-ink-muted">Marca propia · desde $150K MXN</div>
              </div>
              <Arrow className="w-4 h-4 text-ink-muted group-hover:text-cyan" />
            </Link>
          </div>
        </div>
      )}
    </>
  );
}